// โครงหน้าแบรนด์: topbar + แถบซ้าย + เนื้อหาตามเมนู
//
// เนื้อหาแต่ละเมนูเลือกที่นี่ที่เดียว (แทน renderBrandContent() เดิม) เพื่อให้เห็น
// ครบในที่เดียวว่าเมนูไหนไปหน้าไหน และเมนูที่ถูกปิดไว้ถูกดักก่อนทุกกรณี
import { useEffect } from "react";
import { Link, useParams } from "react-router";
import { useAppSelector } from "@/app/hooks";
import { NoAccess } from "@features/auth/components/require-admin";
import { isAdmin } from "@features/auth/lib/roles";
import { AdWatchPage } from "@features/ads/pages/adwatch-page";
import { OnlineAdsPage } from "@features/ads/pages/online-ads-page";
import { CampaignsTablePage } from "@features/campaigns/pages/campaigns-table-page";
import { DiscoverPage } from "@features/discover/pages/discover-page";
import { DiscoverReportPage } from "@features/discover/pages/discover-report-page";
import { DocumentsPage } from "@features/documents/pages/documents-page";
import { FeatureOffView } from "@features/embeds/components/feature-off";
import { FinancePage } from "@features/finance/pages/finance-page";
import { FbEmbedPage } from "@features/embeds/pages/fb-embed-page";
import { MessagesPage } from "@features/embeds/pages/messages-page";
import { KolReportPage } from "@features/kol/pages/kol-report-page";
import { OverviewPage } from "@features/overview/pages/overview-page";
import { Sidebar } from "@features/navigation/components/sidebar";
import { TeamPage } from "@features/team/pages/team-page";
import { Topbar } from "@features/navigation/components/topbar";
import { featureOffFor } from "@features/navigation/model/side-menus";
import { apiErrorMessage } from "@shared/api/client";
import { useBrand } from "../api/use-brands";
import { BrandAssetPage } from "./brand-asset-page";
import { BrandDashboard } from "./brand-dashboard";
import { cn } from "@shared/lib/utils";

export function BrandPage() {
  const { brandId, menuId = "overview", sub } = useParams();
  const { brand, brands, isPending, isError, error } = useBrand(brandId);
  const session = useAppSelector((s) => s.session.current);

  useEffect(() => {
    document.title = brand ? `${brand.name} — Agency Intelligence` : "Brand — Agency Intelligence";
  }, [brand]);

  if (isPending || isError || !brand) {
    return (
      <>
        <Topbar active="myjob" />
        <div className="layout">
          <main className="content" style={{ width: "100%" }}>
            {isPending ? (
              <div className="panel empty-state">
                <h3>กำลังโหลด…</h3>
              </div>
            ) : isError ? (
              <div className="panel empty-state">
                <h3>โหลดรายการแบรนด์ไม่สำเร็จ</h3>
                <p className="muted">{apiErrorMessage(error)}</p>
              </div>
            ) : !isAdmin(session) ? (
              // เข้ามาถึงหน้าแบรนด์ที่ไม่ได้รับสิทธิ์ (พิมพ์ URL เอง / ลิงก์เก่า)
              // server กรองออกไปแล้วจึงหาไม่เจอ — บอกเหตุผลจริง ไม่ใช่ "ยังไม่มีแบรนด์"
              <NoAccess />
            ) : brands.length ? (
              // Admin เห็นทุกแบรนด์อยู่แล้ว หาไม่เจอแปลว่า id นี้ไม่มีจริง (พิมพ์ผิด/ถูกลบไปแล้ว)
              <div className="panel empty-state">
                <h3>ไม่พบแบรนด์นี้</h3>
                <p className="muted">แบรนด์รหัส {brandId} ไม่มีอยู่ หรือถูกปิดจาก My Job ไปแล้ว</p>
                <Link className="btn-primary inline-btn" to="/">
                  กลับไป My Job
                </Link>
              </div>
            ) : (
              <div className="panel empty-state">
                <h3>ยังไม่มีแบรนด์ที่เปิดใช้งาน</h3>
                <p className="muted">
                  ไปที่ Setting → Product แล้วกด Enable แบรนด์ที่ต้องการให้แสดงใน My Job
                </p>
                <Link className="btn-primary inline-btn" to="/setting/product">
                  ไปที่ Setting
                </Link>
              </div>
            )}
          </main>
        </div>
      </>
    );
  }

  const off = featureOffFor(brand, menuId, sub);
  // หน้าที่ฝังระบบอื่นต้องใช้คลาส content-embed (กรอบเต็มพื้นที่ ไม่มี padding ของหน้าปกติ)
  const isEmbed = !!off || menuId === "message" || isFbEmbed(menuId, sub);

  return (
    <>
      <Topbar active="myjob" />
      <div className="layout">
        <Sidebar brand={brand} brands={brands} activeMenuId={menuId} activeSubSlug={sub} />
        <main className={cn("content", isEmbed && "content-embed")} id="content">
          {off ? (
            <FeatureOffView brand={brand} off={off} />
          ) : menuId === "overview" ? (
            <OverviewPage brand={brand} />
          ) : menuId === "discover" && !sub ? (
            <DiscoverPage brand={brand} brands={brands} />
          ) : menuId === "discover" ? (
            <DiscoverReportPage brand={brand} slug={sub} />
          ) : menuId === "ads" && (!sub || sub === "campaigns") ? (
            <CampaignsTablePage brand={brand} />
          ) : menuId === "ads" && sub === "adwatch" ? (
            <AdWatchPage brand={brand} />
          ) : menuId === "ads" && sub === "kol" ? (
            <KolReportPage brand={brand} />
          ) : menuId === "ads" && sub === "online-ads" ? (
            <OnlineAdsPage brand={brand} />
          ) : menuId === "team" ? (
            <TeamPage brand={brand} />
          ) : menuId === "brand-asset" ? (
            <BrandAssetPage brand={brand} />
          ) : menuId === "documents" ? (
            <DocumentsPage brand={brand} />
          ) : menuId === "finance" ? (
            <FinancePage brand={brand} />
          ) : menuId === "message" ? (
            <MessagesPage brand={brand} />
          ) : menuId === "content" && (!sub || sub === "calendar") ? (
            <FbEmbedPage
              brand={brand}
              page="comments.html"
              title="Content Calendar"
              crumb="Content Calendar"
            />
          ) : menuId === "analytics" && (!sub || sub === "facebook-inbox") ? (
            <FbEmbedPage
              brand={brand}
              page="analytics.html"
              title="Facebook Inbox"
              crumb="Analytics / Facebook Inbox"
            />
          ) : (
            <BrandDashboard brand={brand} menuId={menuId} sub={sub} />
          )}
        </main>
      </div>
    </>
  );
}

function isFbEmbed(menuId: string, sub?: string): boolean {
  if (menuId === "discover" && sub) return true;
  if (menuId === "content" && (!sub || sub === "calendar")) return true;
  if (menuId === "analytics" && (!sub || sub === "facebook-inbox")) return true;
  // สองอันนี้ฝังระบบอื่นข้ามโดเมน แต่ใช้กรอบเดียวกัน (ตาราง Campaigns ไม่ใช่)
  if (menuId === "ads" && (sub === "kol" || sub === "online-ads" || sub === "adwatch")) return true;
  return false;
}
